import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert, LocationCard, PageBreadcrumb } from '@repo/shared-client/components/blocks'
import {
  AspectRatio,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Image,
  Rating,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { STATIC_MINIMAP_RANGE_KM } from '@repo/shared-client/mapbox'
import { createFileRoute, type ErrorComponentProps, Link, useNavigate } from '@tanstack/react-router'
import SFJoinButton from '@web/src/components/primatives/sf-join-button/sf-join-button'
import SFMediaImage from '@web/src/components/primatives/sf-media-image/sf-media-image'
import SFProfileBanner from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import SFReviewStatus from '@web/src/components/primatives/sf-review-status/sf-review-status'
import SFRichTextViewer from '@web/src/components/primatives/sf-rich-text-viewer/sf-rich-text-viewer'
import MediaAdminEditDialog from '@web/src/features/media/media-admin-edit/media-admin-edit-dialog'
import MediaAdminEditMenuItems from '@web/src/features/media/media-admin-edit/media-admin-edit-menu-items'
import MediaUploaderDialog from '@web/src/features/media/media-uploader-dialog/media-uploader-dialog'
import SimpleMediaImageViewer from '@web/src/features/media/simple-media-image-viewer/simple-media-image-viewer'
import PlaceDelete from '@web/src/features/place/place-delete/place-delete'
import PlaceTransferModal from '@web/src/features/place/place-transfer-modal/place-transfer-modal'
import { usePlaceCategoryOptions } from '@web/src/features/place/use-place-category-options'
import ReportCreate from '@web/src/features/report/report-create/report-create'
import Reviews, { REVIEWS_QUERY } from '@web/src/features/review/reviews/reviews'
import { useAuth } from '@web/src/hooks/use-auth'
import { useMapBox } from '@web/src/hooks/use-mapbox'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { AttachedMediaInfo, PlaceInfo, ProfileInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { ensureAbsoluteWebUrl } from '@web/src/lib/web-url'
import { EllipsisIcon, FilmIcon, LinkIcon, LocateFixedIcon, SmartphoneIcon } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import type {
  DeleteAttachedMediaMutation,
  DeleteAttachedMediaMutationVariables,
  PlaceAdminApproveMutation,
  PlaceAdminApproveMutationVariables,
  PlaceQuery,
  PlaceQueryVariables,
} from 'types/graphql'
import { placePageHeadFromLoaderData } from './$placeIdOrSlug.seo'

type PlacePageData = NonNullable<PlaceQuery['place']>
type PlaceAttachedMedia = NonNullable<PlacePageData['attachedMedia']>[number]

const PLACE_QUERY: TypedDocumentNode<PlaceQuery, PlaceQueryVariables> = gql`
  query PlaceQuery($placeIdOrSlug: String!) {
    place: place(placeIdOrSlug: $placeIdOrSlug) {
      ...PlaceInfo
      owner {
        ...ProfileInfo
      }
      attachedMedia {
        ...AttachedMediaInfo
      }
    }
  }
  ${PlaceInfo}
  ${AttachedMediaInfo}
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

export const DELETE_IMAGE: TypedDocumentNode<DeleteAttachedMediaMutation, DeleteAttachedMediaMutationVariables> = gql`
  mutation DeleteAttachedMedia($id: String!) {
    deleteAttachedMedia(id: $id) {
      id
    }
  }
`

export const MOVE_MEDIA_UP = gql`
  mutation MoveAttachedMediaUp($id: String!) {
    moveAttachedMediaUp(id: $id) {
      id
    }
  }
`

export const MOVE_MEDIA_DOWN = gql`
  mutation MoveAttachedMediaDown($id: String!) {
    moveAttachedMediaDown(id: $id) {
      id
    }
  }
`

export const MOVE_MEDIA_TO_TOP = gql`
  mutation MoveAttachedMediaToTop($id: String!) {
    moveAttachedMediaToTop(id: $id) {
      id
    }
  }
`

export const MOVE_MEDIA_TO_BOTTOM = gql`
  mutation MoveAttachedMediaToBottom($id: String!) {
    moveAttachedMediaToBottom(id: $id) {
      id
    }
  }
`

export const APPROVE_PLACE: TypedDocumentNode<PlaceAdminApproveMutation, PlaceAdminApproveMutationVariables> = gql`
  mutation PlaceAdminApprove($placeIdOrSlug: String!) {
    placeAdminApprove(placeIdOrSlug: $placeIdOrSlug) {
      ...PlaceInfo
    }
  }
  ${PlaceInfo}
`

function PlacePageError({ error }: ErrorComponentProps) {
  const { t } = useTranslation()
  const resolved = error instanceof Error ? error : new Error(t('An error occurred'))
  return <ErrorAlert error={resolved} />
}

export const Route = createFileRoute('/(public)/(main-layout)/place/$placeIdOrSlug')({
  component: PlacePage,
  errorComponent: PlacePageError,

  head: ({ loaderData }) => placePageHeadFromLoaderData(loaderData),
  loader: async ({ context, params }) => {
    const [{ queryData: placeData, queryRef: placeQueryRef }, { queryData: reviewsData }] = await Promise.all([
      queryAndPreload(context, PLACE_QUERY, {
        variables: { placeIdOrSlug: params.placeIdOrSlug },
        // Expected GraphQL errors (e.g. supporters-only) must not reject the loader;
        // PlacePage renders ErrorAlert from useReadQuery instead of the Oops boundary.
        errorPolicy: 'all',
      }),
      queryAndPreload(context, REVIEWS_QUERY, {
        variables: { filter: { placeIdOrSlug: params.placeIdOrSlug } },
        errorPolicy: 'all',
      }),
    ])

    return {
      queryRef: placeQueryRef,
      placeQueryData: placeData,
      reviewsQueryData: reviewsData,
      place: placeData?.place,
      reviews: reviewsData?.reviews,
    }
  },
})

const galleryThumbCardClass =
  'cursor-pointer border border-border bg-muted w-full transition-colors hover:border-primary hover:bg-muted/80'

function PlacePage() {
  const { t } = useTranslation()
  const params = Route.useParams()
  const loaderData = Route.useLoaderData()
  const client = useApolloClient()
  const { data, error } = useReadQuery(loaderData.queryRef as QueryRef<PlaceQuery>)
  const { currentUser, hasPermission, isAuthenticated } = useAuth()
  const { mapBoxToken } = useMapBox()
  const navigate = useNavigate()

  const [uploadImageModal, setUploadImageModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [approveModal, setApproveModal] = useState(false)
  const [reportModal, setReportModal] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [transferModal, setTransferModal] = useState(false)
  const [imageIndex, setImageIndex] = useState(0)
  const { labelFor } = usePlaceCategoryOptions()

  const handleRefetch = async () => {
    await client.refetchQueries({ include: [PLACE_QUERY] })
  }

  const [approvePlace] = useMutation(APPROVE_PLACE, {
    onCompleted: () => {
      toast.success(t('Place approved'))
      handleRefetch()
    },
    onError: (err) => {
      toast.error(err.message)
    },
  })

  if (error) {
    return <ErrorAlert error={error} />
  }

  const resolvedPlaceQuery = data ?? loaderData.placeQueryData
  if (!resolvedPlaceQuery?.place) {
    return <ErrorAlert error={new Error(t('Place not found'))} />
  }

  const place = resolvedPlaceQuery.place
  const placeName = place.locationExact
    ? (place.location ?? '')
    : t('Near {{location}}', { location: place.location ?? '' })

  const canViewMap = hasPermission('location:view-map') && Boolean(mapBoxToken)
  const distanceRaw = place.distance
  const distanceKm = distanceRaw != null && Number.isFinite(distanceRaw) ? Math.round(distanceRaw) : 0
  const locationCardText = place.locationExact
    ? (place.location ?? '')
    : t('Near {{location}}', { location: place.location ?? '' })
  const locationLat = place.locationLat ?? 0
  const locationLong = place.locationLong ?? 0
  const mapRangeKm = canViewMap ? (place.locationExact ? undefined : STATIC_MINIMAP_RANGE_KM) : undefined

  let reviewValue = 0

  if (place.reviewCount > 0) {
    reviewValue = Math.min(Math.max(0, place.reviewSum / place.reviewCount), 5.0)
  }

  const handleApprovePlace = () => {
    setApproveModal(false)
    approvePlace({ variables: { placeIdOrSlug: place.slug ?? place.id } })
  }

  const mediaCount = place.attachedMedia?.length ?? 0

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-4">
      <PageBreadcrumb
        items={[
          { label: t('Home'), to: '/' },
          { label: t('Place Categories'), to: '/place' },
          { label: t('Discover Places'), to: '/place/list' },
          { label: place.name ?? '' },
        ]}
      />

      <div className="flex w-full flex-col gap-3">
        <div className="flex justify-center self-center">
          {place.canApprove && place.reviewStatus !== 'ACCEPTED' && (
            <SFReviewStatus reviewStatus={place.reviewStatus} size="lg" />
          )}
        </div>

        <div className="grid w-full grid-cols-1 items-center gap-2 md:grid-cols-2">
          <h1 className="line-clamp-1 text-center font-bold text-2xl md:justify-self-start md:text-left md:text-3xl">
            {place?.name}
          </h1>
          <div className="flex flex-wrap items-center justify-center gap-2 md:justify-self-end">
            <div className="flex items-center gap-2">
              <Rating size="sm" value={reviewValue} variant="default" />
              <span className="text-foreground">{reviewValue.toFixed(1)}</span>
            </div>

            {(place.canApprove || place.canDelete || place.canEdit || place.canReport) && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      aria-label={t('Place options')}
                      className="shrink-0"
                      size="icon"
                      type="button"
                      variant="default"
                    />
                  }
                >
                  <EllipsisIcon />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {place.canApprove && place.reviewStatus !== 'ACCEPTED' && (
                    <DropdownMenuItem onClick={() => setApproveModal(true)}>{t('Approve')}</DropdownMenuItem>
                  )}
                  {place.canDelete && (
                    <DropdownMenuItem onClick={() => setDeleteModal(true)}>{t('Delete')}</DropdownMenuItem>
                  )}
                  {place.canEdit && (
                    <DropdownMenuItem
                      onClick={() =>
                        navigate({
                          to: '/place/$placeIdOrSlug/edit',
                          params: { placeIdOrSlug: place.slug ?? place.id } as never,
                        })
                      }
                    >
                      {t('Edit')}
                    </DropdownMenuItem>
                  )}
                  {place.canReport && (
                    <DropdownMenuItem onClick={() => setReportModal(true)}>{t('Report')}</DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        <h2 className="line-clamp-2 font-normal text-foreground">{place?.tagLine}</h2>

        <div className="flex w-full justify-between">
          <div className="flex w-full flex-col items-start">
            <div className="flex items-center gap-2">
              <LocateFixedIcon className="size-4 shrink-0 text-muted-foreground" />
              <Tooltip>
                <TooltipTrigger className="min-w-0" render={<span className="inline-flex min-w-0 cursor-default" />}>
                  <span className="line-clamp-1 text-foreground">{placeName}</span>
                </TooltipTrigger>
                <TooltipContent side="top">{placeName}</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>

        {place.phone && place.phone.length > 0 && (
          <div className="flex w-full justify-between">
            <div className="flex items-center gap-2">
              <SmartphoneIcon className="size-4 shrink-0 text-muted-foreground" />
              <span className="line-clamp-1 text-foreground text-sm">{place.phone}</span>
            </div>
          </div>
        )}

        <div className="flex w-full flex-wrap items-center justify-start gap-2 overflow-hidden py-1">
          <Badge className="shadow-xs" key="place-category">
            {labelFor(place.category)}
          </Badge>
          {place.tags?.map((tag) => (
            <Badge className="shadow-xs" key={tag.id}>
              {tag.name}
            </Badge>
          ))}
        </div>

        <div className="flex w-full flex-col gap-3">
          {place?.attachedMedia && place.attachedMedia.length > 0 && (
            <div className="flex w-full flex-col items-stretch gap-2 md:flex-row">
              {place.attachedMedia.map((attachedMedia, idx) => {
                if (idx > 1) {
                  return null
                }

                const showHeroPairOnMd = place.attachedMedia.length > 1

                return (
                  <Card
                    className={cn(
                      galleryThumbCardClass,
                      'overflow-hidden p-0',
                      showHeroPairOnMd && 'md:w-1/2 md:min-w-0',
                      idx > 0 && 'hidden md:block md:w-1/2'
                    )}
                    key={attachedMedia.id}
                    onClick={() => {
                      setImageIndex(idx)
                      setViewModal(true)
                    }}
                  >
                    <AspectRatio ratio={16 / 9}>
                      <SFMediaImage
                        imageProps={{
                          alt:
                            idx > 0
                              ? t('{{placeName}} — secondary hero image', { placeName: place.name })
                              : t('{{placeName}} — primary hero image', { placeName: place.name }),
                        }}
                        media={attachedMedia}
                      />
                    </AspectRatio>
                  </Card>
                )
              })}
            </div>
          )}

          <div className="flex w-full items-start justify-start gap-2 overflow-x-auto overflow-y-hidden">
            {place.canUploadMedia && (
              <AspectRatio className="w-[125px] min-w-[125px]" ratio={1}>
                <Card
                  className={cn(galleryThumbCardClass, 'flex h-full flex-col items-center justify-center gap-2 p-2')}
                  onClick={() => setUploadImageModal(true)}
                >
                  <FilmIcon className="size-8" />
                  <div className="flex flex-col text-center text-base">
                    <span>{t('Upload')}</span>
                    <span>{t('Media')}</span>
                  </div>
                </Card>
              </AspectRatio>
            )}

            {place.attachedMedia?.map((attachedMedia, idx) => (
              <ImageCard
                idx={idx}
                key={attachedMedia.id}
                media={attachedMedia}
                onImageSelected={(selectedIdx) => {
                  setImageIndex(selectedIdx)
                  setViewModal(true)
                }}
                onRefetch={handleRefetch}
                place={place}
                totalMedia={mediaCount}
              />
            ))}
          </div>
        </div>

        <div className="grid w-full grid-cols-1 items-start gap-4 md:grid-cols-[3fr_1fr]">
          <SFProfileBanner
            lowerTitle={<span className="line-clamp-1 text-sm">{t('Page Maintainer')}</span>}
            profile={place.owner}
            size="lg"
          />
          {place.canTransfer && (
            <Tooltip>
              <TooltipTrigger className="w-full md:w-auto" render={<span className="inline-flex w-full md:w-auto" />}>
                <Button className="w-full" onClick={() => setTransferModal(true)} type="button">
                  {t('Transfer Maintainer')}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">{t('Transfer page ownership to another profile.')}</TooltipContent>
            </Tooltip>
          )}
        </div>

        <Separator />

        <div className="flex w-full flex-col items-start gap-2">
          {place.webLinks?.map((link, idx) => {
            const href = ensureAbsoluteWebUrl(link)
            return (
              <div className="flex w-full flex-col items-start" key={idx}>
                <Link
                  className="inline-flex min-w-0 max-w-full items-center gap-2 text-primary"
                  rel="noopener noreferrer"
                  target="_blank"
                  to={href}
                >
                  <LinkIcon className="size-4 shrink-0" />
                  <span className="line-clamp-1">{link}</span>
                </Link>
              </div>
            )
          })}
        </div>

        <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-[3fr_2fr]">
          <Card>
            <CardHeader>
              <CardTitle>{t('About')}</CardTitle>
            </CardHeader>
            <CardContent>
              <SFRichTextViewer
                className="w-full border-none p-0"
                content={place.description}
                editorStyles="overflow: hidden"
              />
            </CardContent>
          </Card>

          {isAuthenticated ? (
            <LocationCard
              distanceKm={distanceKm}
              latitude={locationLat}
              location={locationCardText}
              longitude={locationLong}
              mapboxToken={mapBoxToken}
              rangeKm={mapRangeKm}
              showSupporterPlaceholder={!canViewMap}
            />
          ) : (
            <div className="relative max-w-[400px] overflow-clip rounded-md border border-border">
              <Image alt={t('Map available to members only')} className="w-full object-cover" src="/map-members.webp" />
            </div>
          )}
        </div>

        <ConfirmDialog
          confirmText={t('Approve')}
          onConfirm={handleApprovePlace}
          onOpenChange={(e) => setApproveModal(e.open)}
          open={approveModal}
          text={t('Are you sure you want to approve this place?')}
          title={t('Are you sure?')}
          variant="basic"
        />
        <PlaceTransferModal
          onComplete={handleRefetch}
          onOpenChange={(e) => setTransferModal(e.open)}
          open={transferModal}
          placeIdOrSlug={place.slug ?? place.id}
        />
        <PlaceDelete onOpenChange={(e) => setDeleteModal(e.open)} open={deleteModal} place={place} />
        <ReportCreate
          onOpenChange={(e) => setReportModal(e.open)}
          open={reportModal}
          targetId={place?.id}
          targetKind="PLACE"
        />
        <MediaUploaderDialog
          onOpenChange={(e) => setUploadImageModal(e.open)}
          onUploadComplete={handleRefetch}
          open={uploadImageModal}
          placeId={place.id}
        />
        {place.attachedMedia && (
          <SimpleMediaImageViewer
            attachedMedia={true}
            imageIndex={imageIndex}
            mediaArray={place.attachedMedia}
            onMediaChange={handleRefetch}
            onOpenChange={(e) => setViewModal(e.open)}
            open={viewModal}
            placeId={place.id}
          />
        )}
        {!isAuthenticated && (
          <div className="flex justify-center py-6">
            <SFJoinButton />
          </div>
        )}
        <Reviews
          canReview={place.canReview}
          onReviewChange={handleRefetch}
          placeIdOrSlug={params.placeIdOrSlug}
          placeOwnerId={place?.ownerId}
          profileId={currentUser?.profile.id}
          scrollableId={null}
        />
      </div>
    </div>
  )
}

type ImageCardProps = {
  onRefetch: () => void | Promise<void>
  place: PlacePageData
  media: PlaceAttachedMedia
  idx: number
  onImageSelected: (idx: number) => void
  totalMedia: number
}

const ImageCard = ({ onRefetch, place, media, idx, onImageSelected, totalMedia }: ImageCardProps) => {
  const { t } = useTranslation()
  const [deleteModal, setDeleteModal] = useState(false)
  const [adminEditModal, setAdminEditModal] = useState(false)

  const [deleteImage] = useMutation(DELETE_IMAGE, {
    onCompleted: () => {
      toast.success(t('Image Deleted'))
      onRefetch()
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
    },
  })

  const [moveUp] = useMutation(MOVE_MEDIA_UP, {
    onCompleted: () => {
      toast.success(t('Media moved up'))
      onRefetch()
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
    },
  })

  const [moveDown] = useMutation(MOVE_MEDIA_DOWN, {
    onCompleted: () => {
      toast.success(t('Media moved down'))
      onRefetch()
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
    },
  })

  const [moveToTop] = useMutation(MOVE_MEDIA_TO_TOP, {
    onCompleted: () => {
      toast.success(t('Media moved to top'))
      onRefetch()
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
    },
  })

  const [moveToBottom] = useMutation(MOVE_MEDIA_TO_BOTTOM, {
    onCompleted: () => {
      toast.success(t('Media moved to bottom'))
      onRefetch()
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
    },
  })

  const handleDeleteImage = () => {
    setDeleteModal(false)
    deleteImage({ variables: { id: media.id } })
  }

  return (
    <div className="relative shrink-0">
      <AspectRatio className="relative w-[125px] min-w-[125px]" ratio={1}>
        <Card className={cn(galleryThumbCardClass, 'h-full overflow-hidden p-0')} onClick={() => onImageSelected(idx)}>
          <SFMediaImage imageProps={{ alt: t('Additional place media image') }} media={media} />
        </Card>
      </AspectRatio>
      {place.canDeleteMedia ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            className="absolute top-0.5 right-0.5 z-10"
            onClick={(e) => e.stopPropagation()}
            render={
              <Button
                aria-label={t('Media options')}
                className="rounded-full border-black"
                size="icon-xs"
                type="button"
                variant="default"
              />
            }
          >
            <EllipsisIcon className="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
            {idx > 0 ? (
              <DropdownMenuItem onClick={() => moveToTop({ variables: { id: media.id } })}>
                {t('Move to Top')}
              </DropdownMenuItem>
            ) : null}
            {idx > 0 ? (
              <DropdownMenuItem onClick={() => moveUp({ variables: { id: media.id } })}>
                {t('Move Up')}
              </DropdownMenuItem>
            ) : null}
            {idx < totalMedia - 1 ? (
              <DropdownMenuItem onClick={() => moveDown({ variables: { id: media.id } })}>
                {t('Move Down')}
              </DropdownMenuItem>
            ) : null}
            {idx < totalMedia - 1 ? (
              <DropdownMenuItem onClick={() => moveToBottom({ variables: { id: media.id } })}>
                {t('Move to Bottom')}
              </DropdownMenuItem>
            ) : null}

            <MediaAdminEditMenuItems mediaId={media.id} onOpen={() => setAdminEditModal(true)} />
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setDeleteModal(true)} variant="destructive">
              {t('Delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      <ConfirmDialog
        onConfirm={handleDeleteImage}
        onOpenChange={(e) => setDeleteModal(e.open)}
        open={deleteModal}
        text={t('Are you sure you want to delete this image?')}
        title={t('Are you sure?')}
        variant="delete"
      />
      <MediaAdminEditDialog
        isAttached
        mediaId={media.id}
        onMediaChange={onRefetch}
        onOpenChange={(e) => setAdminEditModal(e.open)}
        open={adminEditModal}
      />
    </div>
  )
}
